import type { ComponentType } from 'react';
import Tooltip from './Tooltip';

export interface SegmentedOption<K extends string = string> {
  key: K;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  /** Tooltip / a11y text. Falls back to the label. */
  title?: string;
}

export interface SegmentedToggleProps<K extends string = string> {
  options: SegmentedOption<K>[];
  value: K;
  onChange: (key: K) => void;
  /** Greys the whole control out — e.g. the map board while pins are being edited. */
  disabled?: boolean;
  /** Reason shown on hover when disabled, so the control isn't dead for no visible reason. */
  disabledTitle?: string;
  className?: string;
  'aria-label'?: string;
}

/**
 * Compact segmented control for switching a page between two (or more) presentations of the
 * SAME data — as opposed to PageTabs, which switches between different surfaces.
 *
 * Sized to sit inside a page toolbar next to the title, so labels collapse to icons on narrow
 * screens rather than wrapping the toolbar onto a second line. Rendered as a radiogroup: it
 * chooses a view, it does not navigate, so tabs/links would both be the wrong semantics.
 */
export default function SegmentedToggle<K extends string = string>({
  options,
  value,
  onChange,
  disabled = false,
  disabledTitle,
  className = '',
  'aria-label': ariaLabel,
}: SegmentedToggleProps<K>) {
  if (options.length < 2) return null;
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`inline-flex flex-none items-center gap-0.5 rounded-lg border border-border bg-card p-0.5 ${
        disabled ? 'opacity-50' : ''
      } ${className}`}
    >
      {options.map((o) => {
        const active = o.key === value;
        const Icon = o.icon;
        // The tooltip lives on Tooltip's wrapper, not the button: a disabled button emits no
        // mouse events at all, so `pointer-events-none` hands the hover to the wrapper and
        // the "why is this dead" reason still shows.
        return (
          <Tooltip key={o.key} label={disabled ? disabledTitle : o.title || o.label} side="bottom">
            <button
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={o.label}
              disabled={disabled}
              onClick={() => !active && onChange(o.key)}
              // The selected segment is a SLATE pill on a white track, not a brand fill.
              //
              // Brand-filled read as loud enough — too loud, and worse, wrong: `--brand` is a
              // vermilion that sits a few degrees from `--destructive`, so on the alert board a
              // selected view chip and a critical alert wore nearly the same colour and the one
              // that meant "danger" stopped being the only red thing on the page. A control
              // saying WHICH OF TWO VIEWS you are on has no business borrowing the palette's
              // one alarm colour.
              //
              // Inverting the contrast is what keeps it readable across a room without it: the
              // track went white and the selected pill went to the grey the track used to be,
              // which is a bigger step than the raised-panel version that failed here before —
              // that one was a light pill on an equally light track.
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:pointer-events-none sm:text-[13px] ${
                active
                  ? 'bg-muted text-foreground shadow-sm ring-1 ring-inset ring-border'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
              }`}
            >
              {Icon && <Icon className="h-4 w-4 flex-none" />}
              {/* Dropped on the narrowest screens when there is a symbol to carry the meaning
                  (the tooltip has the rest). A text-only option always shows its label. */}
              <span className={`whitespace-nowrap ${Icon ? 'hidden sm:inline' : ''}`}>{o.label}</span>
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}
